import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { subscriptionAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Building2, CreditCard, Users, FileText, Wrench, BarChart3,
  ArrowRight, Check, Star, TrendingUp, TrendingDown, ChevronRight,
  Loader2,
} from 'lucide-react';

const TEAL = '#00a99d';
const DARK_NAVY = '#1a1a2e';
const GRAY_TEXT = '#595959';

const plans = [
  { tier: 'BASIC', name: 'Basic', price: 29, desc: 'Perfect for getting started with a single property.', features: ['1 property', 'Up to 2 units', 'Online rent collection', 'Tenant portal', 'Basic reporting', 'Email support'] },
  { tier: 'PRO', name: 'Pro', price: 59, popular: true, desc: 'Great for landlords managing one property with more units.', features: ['1 property', 'Up to 5 units', 'Online rent collection', 'Tenant portal', 'Advanced analytics', 'Priority support'] },
  { tier: 'EXPERT', name: 'Expert', price: 99, desc: 'For portfolio landlords with multiple properties.', features: ['Up to 5 properties', 'Up to 20 units', 'All Pro features', 'Custom branding', 'Lease automation', 'Audit logs'] },
  { tier: 'ENTERPRISE', name: 'Enterprise', price: 299, desc: 'Unlimited scale for property management companies.', features: ['Unlimited properties', 'Unlimited units', 'All Expert features', 'API access', 'Dedicated support', 'SLA guarantee'] },
];

const trustNames = ['Sunset Properties', 'Metro Living', 'Bay Area Rentals', 'Urban Nest', 'Prime Realty'];

const benefits = [
  { icon: Building2, title: 'Property Management', desc: 'Manage multiple properties and units from one dashboard. Track occupancy, maintenance, and revenue.' },
  { icon: CreditCard, title: 'Online Rent Collection', desc: 'Collect rent via card or ACH bank transfer through Stripe Connect. Automatic receipts and tracking.' },
  { icon: Users, title: 'Tenant Portal', desc: 'Tenants can view leases, pay rent, submit maintenance requests, and access documents online.' },
  { icon: FileText, title: 'Lease Management', desc: 'Create, track, and renew leases. Automatic expiry alerts and renewal workflows.' },
  { icon: Wrench, title: 'Maintenance Tracking', desc: 'Tenants submit requests, you prioritize and resolve. Full audit trail of every ticket.' },
  { icon: BarChart3, title: 'Financial Reports', desc: 'Rent roll, income statements, and occupancy analytics. Export to CSV for your accountant.' },
];

const testimonials = [
  { quote: 'Realty JAG cut our rent collection time by 70%. Tenants love the online portal and we never chase payments anymore.', name: 'Jessica Palmer', title: 'Owner, Sunset Properties', initial: 'J' },
  { quote: "Finally, a property management tool that's simple and affordable. The maintenance tracking alone is worth it.", name: 'Marcus Wright', title: 'Manager, Metro Living', initial: 'M' },
  { quote: 'We manage 45 units and Realty JAG handles everything. The Stripe integration is seamless.', name: 'Amanda Chen', title: 'CEO, Bay Area Rentals', initial: 'A' },
];

const LandingPage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loadingTier, setLoadingTier] = useState(null);
  const [currentTier, setCurrentTier] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'LANDLORD') {
      subscriptionAPI.getMyStatus().then(res => {
        if (res.data.is_active) setCurrentTier(res.data.tier);
      }).catch(() => {});
    }
  }, [isAuthenticated, user]);

  const handleSelectPlan = async (tier) => {
    if (!isAuthenticated) { navigate(`/register?plan=${tier}`); return; }
    if (user?.role !== 'LANDLORD') { toast.error('Only landlord accounts can subscribe'); return; }
    if (currentTier === tier) return;
    setLoadingTier(tier);
    try {
      const endpoint = currentTier ? '/subscriptions/change-plan' : '/subscriptions/checkout';
      const params = new URLSearchParams({ tier, origin_url: window.location.origin });
      const res = await api.post(`${endpoint}?${params}`);
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally { setLoadingTier(null); }
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif", color: '#1a1a2e' }} data-testid="landing-page">

      {/* ─── NAVBAR ─── */}
      <nav
        style={{
          position: 'sticky', top: 0, zIndex: 50,
          background: '#fff',
          boxShadow: scrolled ? '0 1px 12px rgba(0,0,0,0.08)' : 'none',
          transition: 'box-shadow 0.3s',
        }}
      >
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }} data-testid="nav-logo">
            <Building2 style={{ width: 28, height: 28, color: TEAL }} />
            <span style={{ fontSize: 22, fontWeight: 700, color: DARK_NAVY, letterSpacing: '-0.02em' }}>
              Realty <span style={{ color: TEAL }}>JAG</span>
            </span>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} className="hidden md:flex">
            {[['Features', 'features'], ['How It Works', 'how-it-works'], ['Testimonials', 'testimonials'], ['Pricing', 'pricing']].map(([label, id]) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                style={{ padding: '8px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: GRAY_TEXT, fontWeight: 500, borderRadius: 8, transition: 'color 0.2s' }}
                onMouseEnter={e => e.target.style.color = TEAL}
                onMouseLeave={e => e.target.style.color = GRAY_TEXT}
                data-testid={`nav-${id}-link`}
              >
                {label}
              </button>
            ))}
            <Link to="/login" style={{ padding: '8px 16px', fontSize: 15, color: GRAY_TEXT, fontWeight: 500, textDecoration: 'none' }} data-testid="nav-login-link">Login</Link>
          </div>
          <Link
            to="/register"
            data-testid="nav-start-trial-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '10px 22px', borderRadius: 50, background: TEAL, color: '#fff',
              fontSize: 14, fontWeight: 600, textDecoration: 'none', transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => e.target.style.opacity = '0.9'}
            onMouseLeave={e => e.target.style.opacity = '1'}
          >
            Start Free Trial
          </Link>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section style={{ background: '#fff', overflow: 'hidden' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '64px 24px 80px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 48 }}>
          <div style={{ flex: '1 1 480px', minWidth: 320 }}>
            <div
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: `${TEAL}12`, border: `1px solid ${TEAL}30`,
                borderRadius: 50, padding: '6px 16px', marginBottom: 24,
              }}
              data-testid="hero-trust-badge"
            >
              <Star style={{ width: 14, height: 14, fill: TEAL, color: TEAL }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: TEAL }}>Trusted by 200+ Landlords</span>
            </div>
            <h1 style={{ fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 800, lineHeight: 1.12, margin: '0 0 20px', color: DARK_NAVY }} data-testid="hero-headline">
              Take Control of Your <span style={{ color: TEAL }}>Rental Properties</span>
            </h1>
            <p style={{ fontSize: 18, lineHeight: 1.7, color: GRAY_TEXT, maxWidth: 520, marginBottom: 32 }}>
              The all-in-one platform to collect rent online, manage leases, track maintenance, and grow your portfolio. Everything you need to run a professional rental business.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
              <Link
                to="/register"
                data-testid="hero-start-trial-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '14px 28px', borderRadius: 10, background: TEAL, color: '#fff',
                  fontSize: 16, fontWeight: 600, textDecoration: 'none', transition: 'transform 0.15s, opacity 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.92'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                Start 7-Day Free Trial <ArrowRight style={{ width: 18, height: 18 }} />
              </Link>
              <button
                onClick={() => scrollTo('how-it-works')}
                data-testid="hero-how-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '14px 28px', borderRadius: 10, background: 'transparent',
                  border: `2px solid #e5e7eb`, color: DARK_NAVY, fontSize: 16, fontWeight: 600,
                  cursor: 'pointer', transition: 'border-color 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = TEAL}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#e5e7eb'}
              >
                See How It Works
              </button>
            </div>
            <div style={{ display: 'flex', gap: 24, marginTop: 20, flexWrap: 'wrap' }}>
              {['No credit card required', 'Cancel anytime'].map(t => (
                <span key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: GRAY_TEXT }}>
                  <Check style={{ width: 16, height: 16, color: '#22c55e' }} /> {t}
                </span>
              ))}
            </div>
          </div>
          <div style={{ flex: '1 1 440px', minWidth: 300, position: 'relative' }}>
            <img
              src="https://images.pexels.com/photos/2462015/pexels-photo-2462015.jpeg?auto=compress&cs=tinysrgb&w=800"
              alt="Modern apartment building"
              data-testid="hero-image"
              style={{ width: '100%', height: 420, objectFit: 'cover', borderRadius: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.12)' }}
            />
            {/* Floating stat cards */}
            <div
              style={{
                position: 'absolute', top: 24, right: -16,
                background: '#fff', borderRadius: 14, padding: '14px 18px',
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', gap: 12,
                animation: 'float 3s ease-in-out infinite',
              }}
              data-testid="hero-stat-1"
            >
              <div style={{ width: 40, height: 40, borderRadius: 10, background: `${TEAL}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrendingUp style={{ width: 20, height: 20, color: TEAL }} />
              </div>
              <div>
                <p style={{ fontSize: 20, fontWeight: 700, color: DARK_NAVY, margin: 0 }}>+92%</p>
                <p style={{ fontSize: 12, color: GRAY_TEXT, margin: 0 }}>On-Time Rent Collection</p>
              </div>
            </div>
            <div
              style={{
                position: 'absolute', bottom: 32, left: -16,
                background: '#fff', borderRadius: 14, padding: '14px 18px',
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', gap: 12,
                animation: 'float 3s ease-in-out infinite 1.5s',
              }}
              data-testid="hero-stat-2"
            >
              <div style={{ width: 40, height: 40, borderRadius: 10, background: '#22c55e15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrendingDown style={{ width: 20, height: 20, color: '#22c55e' }} />
              </div>
              <div>
                <p style={{ fontSize: 20, fontWeight: 700, color: DARK_NAVY, margin: 0 }}>-40%</p>
                <p style={{ fontSize: 12, color: GRAY_TEXT, margin: 0 }}>Admin Time Saved</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── TRUST BAR ─── */}
      <section style={{ background: '#f9fafb', padding: '28px 24px' }}>
        <p style={{ textAlign: 'center', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: '#9ca3af', fontWeight: 600, marginBottom: 16 }}>
          Trusted by Landlords Nationwide
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 40 }}>
          {trustNames.map(n => (
            <span key={n} style={{ fontSize: 16, fontWeight: 600, color: '#d1d5db', letterSpacing: 0.5 }}>{n}</span>
          ))}
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section id="features" style={{ background: '#fff', padding: '80px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: TEAL, marginBottom: 12 }}>FEATURES</p>
            <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 38px)', fontWeight: 800, color: DARK_NAVY, marginBottom: 12 }} data-testid="features-heading">
              Everything You Need to Manage Rentals
            </h2>
            <p style={{ fontSize: 17, color: GRAY_TEXT, maxWidth: 600, margin: '0 auto' }}>
              From collecting rent to tracking maintenance, Realty JAG handles it all so you can focus on growing your portfolio.
            </p>
          </div>

          {/* Feature Block 1 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 48, marginBottom: 64 }}>
            <div style={{ flex: '1 1 400px', minWidth: 280 }}>
              <img
                src="https://images.pexels.com/photos/6969649/pexels-photo-6969649.jpeg?auto=compress&cs=tinysrgb&w=700"
                alt="Online rent payment"
                style={{ width: '100%', height: 360, objectFit: 'cover', borderRadius: 16 }}
                data-testid="feature-image-1"
              />
            </div>
            <div style={{ flex: '1 1 400px', minWidth: 280 }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: `${TEAL}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                <CreditCard style={{ width: 22, height: 22, color: TEAL }} />
              </div>
              <h3 style={{ fontSize: 26, fontWeight: 700, color: DARK_NAVY, marginBottom: 12 }}>Collect Rent Online</h3>
              <p style={{ fontSize: 16, color: GRAY_TEXT, lineHeight: 1.7, marginBottom: 20 }}>
                Accept rent payments via credit card or ACH bank transfer through Stripe. Automatic receipts, late fee tracking, and payment reminders sent to tenants.
              </p>
              {['Automatic payment reminders', 'Late fee automation', 'Real-time payment tracking'].map(item => (
                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <Check style={{ width: 18, height: 18, color: TEAL, flexShrink: 0 }} />
                  <span style={{ fontSize: 15, color: GRAY_TEXT }}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Feature Block 2 */}
          <div style={{ display: 'flex', flexWrap: 'wrap-reverse', alignItems: 'center', gap: 48 }}>
            <div style={{ flex: '1 1 400px', minWidth: 280 }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: `${TEAL}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                <BarChart3 style={{ width: 22, height: 22, color: TEAL }} />
              </div>
              <h3 style={{ fontSize: 26, fontWeight: 700, color: DARK_NAVY, marginBottom: 12 }}>Track Everything in Real-Time</h3>
              <p style={{ fontSize: 16, color: GRAY_TEXT, lineHeight: 1.7, marginBottom: 20 }}>
                Get instant visibility into occupancy rates, revenue, expenses, and maintenance across all your properties from one dashboard.
              </p>
              {['Real-time occupancy tracking', 'Automated financial reports', 'Maintenance request management'].map(item => (
                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <Check style={{ width: 18, height: 18, color: TEAL, flexShrink: 0 }} />
                  <span style={{ fontSize: 15, color: GRAY_TEXT }}>{item}</span>
                </div>
              ))}
            </div>
            <div style={{ flex: '1 1 400px', minWidth: 280 }}>
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&w=700&q=80"
                alt="Dashboard analytics"
                style={{ width: '100%', height: 360, objectFit: 'cover', borderRadius: 16 }}
                data-testid="feature-image-2"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─── BENEFITS GRID (Dark) ─── */}
      <section style={{ background: DARK_NAVY, padding: '80px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: TEAL, marginBottom: 12 }}>BENEFITS</p>
            <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 38px)', fontWeight: 800, color: '#fff', marginBottom: 12 }} data-testid="benefits-heading">
              One Platform, Complete Control
            </h2>
            <p style={{ fontSize: 17, color: '#94a3b8', maxWidth: 560, margin: '0 auto' }}>
              Realty JAG combines everything you need to manage your rentals efficiently.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
            {benefits.map(b => (
              <div
                key={b.title}
                style={{
                  background: '#232340', borderRadius: 16, padding: '28px 24px',
                  border: '1px solid rgba(255,255,255,0.06)', transition: 'border-color 0.3s',
                }}
                data-testid={`benefit-${b.title.toLowerCase().replace(/\s+/g, '-')}`}
                onMouseEnter={e => e.currentTarget.style.borderColor = `${TEAL}50`}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'}
              >
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: `${TEAL}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <b.icon style={{ width: 22, height: 22, color: TEAL }} />
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 8 }}>{b.title}</h3>
                <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.65, margin: 0 }}>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section id="how-it-works" style={{ background: '#fff', padding: '80px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: TEAL, marginBottom: 12 }}>HOW IT WORKS</p>
            <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 38px)', fontWeight: 800, color: DARK_NAVY }} data-testid="how-heading">
              Get Started in 3 Simple Steps
            </h2>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 24, alignItems: 'flex-start' }}>
            {[
              { num: '1', title: 'Sign Up', desc: 'Create your account in under 2 minutes. No credit card required.' },
              { num: '2', title: 'Add Your Properties', desc: "Import your properties, units, and tenants. We'll set up everything automatically." },
              { num: '3', title: 'Collect & Manage', desc: 'Start collecting rent online, track maintenance, and grow your portfolio.' },
            ].map((step, i) => (
              <React.Fragment key={step.num}>
                {i > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', paddingTop: 40 }} className="hidden md:flex">
                    <ChevronRight style={{ width: 28, height: 28, color: TEAL }} />
                  </div>
                )}
                <div
                  style={{
                    flex: '0 1 300px', background: '#fff', border: '1px solid #e5e7eb',
                    borderRadius: 16, padding: '32px 24px', textAlign: 'center',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                  }}
                  data-testid={`step-${step.num}`}
                >
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%', background: TEAL,
                    color: '#fff', fontSize: 22, fontWeight: 800, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
                  }}>
                    {step.num}
                  </div>
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: DARK_NAVY, marginBottom: 8 }}>{step.title}</h3>
                  <p style={{ fontSize: 15, color: GRAY_TEXT, lineHeight: 1.6, margin: 0 }}>{step.desc}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section id="testimonials" style={{ background: '#f9fafb', padding: '80px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: TEAL, marginBottom: 12 }}>TESTIMONIALS</p>
            <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 38px)', fontWeight: 800, color: DARK_NAVY }} data-testid="testimonials-heading">
              Loved by Property Managers
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
            {testimonials.map(t => (
              <div
                key={t.name}
                style={{
                  background: '#fff', borderRadius: 16, padding: '28px 24px',
                  border: '1px solid #e5e7eb', boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                }}
                data-testid={`testimonial-${t.initial.toLowerCase()}`}
              >
                <div style={{ display: 'flex', gap: 3, marginBottom: 16 }}>
                  {[...Array(5)].map((_, i) => <Star key={i} style={{ width: 16, height: 16, fill: '#f59e0b', color: '#f59e0b' }} />)}
                </div>
                <p style={{ fontSize: 15, color: DARK_NAVY, lineHeight: 1.7, marginBottom: 20, fontStyle: 'italic' }}>
                  "{t.quote}"
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', background: TEAL,
                    color: '#fff', fontWeight: 700, fontSize: 16, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    {t.initial}
                  </div>
                  <div>
                    <p style={{ fontSize: 15, fontWeight: 700, color: DARK_NAVY, margin: 0 }}>{t.name}</p>
                    <p style={{ fontSize: 13, color: GRAY_TEXT, margin: 0 }}>{t.title}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section id="pricing" style={{ background: '#fff', padding: '80px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: TEAL, marginBottom: 12 }}>PRICING</p>
            <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 38px)', fontWeight: 800, color: DARK_NAVY, marginBottom: 12 }} data-testid="pricing-heading">
              Simple, Transparent Pricing
            </h2>
            <p style={{ fontSize: 17, color: GRAY_TEXT, maxWidth: 520, margin: '0 auto' }}>
              Choose the plan that fits your portfolio. All plans include online rent collection, tenant portal, and maintenance tracking.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
            {plans.map(plan => {
              const isCurrent = currentTier === plan.tier;
              return (
                <div
                  key={plan.tier}
                  data-testid={`plan-card-${plan.tier.toLowerCase()}`}
                  style={{
                    position: 'relative', background: '#fff', borderRadius: 16,
                    padding: '32px 24px', display: 'flex', flexDirection: 'column',
                    border: plan.popular ? `2px solid ${TEAL}` : '1px solid #e5e7eb',
                    boxShadow: plan.popular ? `0 8px 30px ${TEAL}15` : '0 2px 12px rgba(0,0,0,0.04)',
                  }}
                >
                  {plan.popular && (
                    <div style={{
                      position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)',
                      background: TEAL, color: '#fff', fontSize: 12, fontWeight: 700,
                      padding: '4px 14px', borderRadius: 50, textTransform: 'uppercase', letterSpacing: 1,
                    }}>
                      Most Popular
                    </div>
                  )}
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: DARK_NAVY, marginBottom: 4 }}>{plan.name}</h3>
                  <p style={{ fontSize: 14, color: GRAY_TEXT, marginBottom: 16 }}>{plan.desc}</p>
                  <div style={{ marginBottom: 20 }}>
                    <span style={{ fontSize: 40, fontWeight: 800, color: DARK_NAVY }}>${plan.price}</span>
                    <span style={{ fontSize: 15, color: GRAY_TEXT }}>/month</span>
                    <div style={{ fontSize: 13, color: TEAL, fontWeight: 600, marginTop: 4 }}>7 days free</div>
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', flex: 1 }}>
                    {plan.features.map(f => (
                      <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
                        <Check style={{ width: 16, height: 16, color: TEAL, marginTop: 2, flexShrink: 0 }} />
                        <span style={{ fontSize: 14, color: GRAY_TEXT }}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handleSelectPlan(plan.tier)}
                    disabled={isCurrent || loadingTier === plan.tier}
                    data-testid={`select-plan-${plan.tier.toLowerCase()}-btn`}
                    style={{
                      width: '100%', padding: '12px 0', borderRadius: 10, cursor: 'pointer',
                      fontSize: 15, fontWeight: 600, border: 'none', transition: 'opacity 0.2s',
                      background: plan.popular ? TEAL : 'transparent',
                      color: plan.popular ? '#fff' : DARK_NAVY,
                      ...(plan.popular ? {} : { border: `1.5px solid #e5e7eb` }),
                      opacity: isCurrent ? 0.5 : 1,
                    }}
                    onMouseEnter={e => { if (!isCurrent) e.target.style.opacity = '0.85'; }}
                    onMouseLeave={e => { if (!isCurrent) e.target.style.opacity = isCurrent ? '0.5' : '1'; }}
                  >
                    {loadingTier === plan.tier ? 'Redirecting...' : isCurrent ? 'Current Plan' : 'Start Free Trial'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section style={{ background: `linear-gradient(135deg, ${TEAL}, #008f85)`, padding: '80px 24px' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 38px)', fontWeight: 800, color: '#fff', marginBottom: 16 }} data-testid="cta-heading">
            Ready to Streamline Your Rental Business?
          </h2>
          <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.85)', marginBottom: 32 }}>
            Join hundreds of landlords who use Realty JAG to save time, collect rent faster, and keep tenants happy.
          </p>
          <Link
            to="/register"
            data-testid="cta-start-trial-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '16px 36px', borderRadius: 10, background: '#fff', color: TEAL,
              fontSize: 17, fontWeight: 700, textDecoration: 'none', transition: 'transform 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
          >
            Start Your Free Trial Today <ArrowRight style={{ width: 18, height: 18 }} />
          </Link>
          <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 24, marginTop: 24 }}>
            {['No setup fees', 'Cancel anytime', 'Stripe-powered payments'].map(t => (
              <span key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: 'rgba(255,255,255,0.9)' }}>
                <Check style={{ width: 16, height: 16 }} /> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer style={{ background: DARK_NAVY, padding: '48px 24px 32px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 32, marginBottom: 32 }}>
            {/* Brand + Address */}
            <div style={{ maxWidth: 300 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <Building2 style={{ width: 22, height: 22, color: TEAL }} />
                <span style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>Realty <span style={{ color: TEAL }}>JAG</span></span>
              </div>
              <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.7, margin: 0 }}>
                350 N Federal Hwy, 715S<br />
                Boynton Beach, FL 33435<br />
                <a href="mailto:contact@realtyjag.com" style={{ color: TEAL, textDecoration: 'none' }}>contact@realtyjag.com</a>
              </p>
            </div>
            {/* Links */}
            <div style={{ display: 'flex', gap: 48, flexWrap: 'wrap' }}>
              <div>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Product</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[['Features', () => scrollTo('features')], ['Pricing', () => scrollTo('pricing')], ['How It Works', () => scrollTo('how-it-works')]].map(([label, fn]) => (
                    <button key={label} onClick={fn} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 14, cursor: 'pointer', textAlign: 'left', padding: 0 }}
                      onMouseEnter={e => e.target.style.color = '#fff'} onMouseLeave={e => e.target.style.color = '#94a3b8'}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Account</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 14, cursor: 'pointer', textAlign: 'left', padding: 0 }}
                    onMouseEnter={e => e.target.style.color = '#fff'} onMouseLeave={e => e.target.style.color = '#94a3b8'}>
                    Login
                  </button>
                  <button onClick={() => navigate('/pricing')} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 14, cursor: 'pointer', textAlign: 'left', padding: 0 }}
                    onMouseEnter={e => e.target.style.color = '#fff'} onMouseLeave={e => e.target.style.color = '#94a3b8'}>
                    Start Free Trial
                  </button>
                </div>
              </div>
              <div>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Legal</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button onClick={() => navigate('/privacy')} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 14, cursor: 'pointer', textAlign: 'left', padding: 0 }}
                    onMouseEnter={e => e.target.style.color = '#fff'} onMouseLeave={e => e.target.style.color = '#94a3b8'}>
                    Privacy Policy
                  </button>
                  <button onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 14, cursor: 'pointer', textAlign: 'left', padding: 0 }}
                    onMouseEnter={e => e.target.style.color = '#fff'} onMouseLeave={e => e.target.style.color = '#94a3b8'}>
                    Terms of Service
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Bottom bar */}
          <div style={{ borderTop: '1px solid #1e293b', paddingTop: 20, textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
              &copy; {new Date().getFullYear()} Realty JAG LLC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Float animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
